import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ApiError, authApi } from '../api/client';
import { Field, LanguageSwitcher, Notice, PasswordInput } from '../components/ui';
import { useI18n } from '../i18n';

/**
 * Passwort vergessen - ausschließlich für Kundenkonten mit E-Mail-Adresse.
 * Callerzugänge haben diese Funktion nicht; darauf weist die Seite hin.
 */
export function ForgotPasswordPage(): JSX.Element {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    // Die Antwort ist immer dieselbe - auch wenn es das Konto nicht gibt.
    await authApi.requestPasswordReset(email).catch(() => undefined);
    setDone(true);
    setBusy(false);
  };

  return (
    <main className="page page-narrow">
      <header className="page-header">
        <h1>{t('forgot.title')}</h1>
        <LanguageSwitcher />
      </header>

      {done ? (
        <Notice tone="success">{t('forgot.done')}</Notice>
      ) : (
        <>
          <p className="lead">{t('forgot.text')}</p>
          <form onSubmit={submit} className="form">
            <Field label={t('login.email')} hint={t('forgot.callerHint')}>
              <input
                type="email"
                value={email}
                autoComplete="email"
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>
            <button type="submit" className="primary" disabled={busy}>
              {t('forgot.submit')}
            </button>
          </form>
        </>
      )}

      <nav className="page-links">
        <Link to="/anmelden">{t('action.back')}</Link>
      </nav>
    </main>
  );
}

export function ResetPasswordPage(): JSX.Element {
  const { t, tError } = useI18n();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await authApi.confirmPasswordReset(token, password);
      setDone(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="page page-narrow">
      <header className="page-header">
        <h1>{t('reset.title')}</h1>
        <LanguageSwitcher />
      </header>

      {done ? (
        <Notice tone="success">{t('reset.done')}</Notice>
      ) : (
        <form onSubmit={submit} className="form">
          <Field label={t('reset.password')}>
            <PasswordInput value={password} onChange={setPassword} autoComplete="new-password" />
          </Field>
          {error ? <Notice tone="error">{error}</Notice> : null}
          <button type="submit" className="primary" disabled={busy || token.length === 0}>
            {t('reset.submit')}
          </button>
        </form>
      )}

      <nav className="page-links">
        <Link to="/anmelden">{t('action.login')}</Link>
      </nav>
    </main>
  );
}
